import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getGlobalPreloadedAudio, removeGlobalPreloadedAudio } from '@/hooks/useAudioPreloader';
import { Button } from '@/components/ui/button';

interface Track {
  id: string;
  name: string;
  artist: string;
  album: string;
  imageUrl: string;
  previewUrl: string | null;
  spotifyUrl: string;
  uri: string;
  durationMs?: number;
  youtubeId?: string;
}

interface UseAudioPlaybackReturn {
  currentTrack: Track | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  playbackMode: 'html5' | 'youtube' | 'unknown';
  playTrack: (track: Track) => Promise<void>;
  togglePlayPause: () => void;
  stopPlayback: () => void;
  handleProgress: (time: number, totalDuration: number) => void;
}

export const useAudioPlayback = (onTrackEnd?: () => void): UseAudioPlaybackReturn => {
  const { toast } = useToast();
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playbackMode, setPlaybackMode] = useState<'html5' | 'youtube' | 'unknown'>('unknown');
  
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Check for audio permission from localStorage
  const hasAudioPermission = () => {
    const stored = localStorage.getItem('audio_permission_granted');
    return stored === 'true';
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const playTrack = useCallback(async (track: Track) => {
    console.log('🎵 [useAudioPlayback] playTrack called with:', {
      trackName: track.name,
      artist: track.artist,
      hasPreviewUrl: !!track.previewUrl,
      previewUrl: track.previewUrl
    });
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    console.log('🔐 [useAudioPlayback] Auth session:', { 
      hasSession: !!session, 
      userId: session?.user?.id 
    });

    // Defensive check: ensure track has required fields
    if (!track.id || !track.name || !track.artist) {
      toast({
        title: "Invalid Track",
        description: "This track is missing required information.",
        variant: "destructive",
      });
      return;
    }

    // Stop current audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }

    // Reset state
    setCurrentTrack(track);
    setIsPlaying(false);
    setCurrentTime(0);
    setProgress(0);
    setDuration(30);

    // PRIMARY PATH: HTML5 Audio with Spotify preview
    if (track.previewUrl) {
      setPlaybackMode('html5');

      try {
        // Try to get preloaded audio first (instant playback)
        let audio = getGlobalPreloadedAudio(track.id);
        
        if (audio) {
          audioRef.current = audio;
        } else {
          audio = new Audio(track.previewUrl);
          audioRef.current = audio;
        }

        // Set up event listeners for progress tracking
        const handleTimeUpdate = () => {
          if (!audio) return;
          const current = audio.currentTime;
          const dur = audio.duration || 30;
          setCurrentTime(current);
          setDuration(dur);
          setProgress((current / dur) * 100);
        };

        const handleEnded = () => {
          setIsPlaying(false);
          if (onTrackEnd) onTrackEnd();
        };

        const handleLoadedMetadata = () => {
          if (!audio) return;
          const dur = audio.duration || 30;
          setDuration(dur);
        };

        const handleError = async (e: Event) => {
          // Fall back to YouTube
          await fallbackToYouTube(track);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('error', handleError);

        // Attempt to play
        console.log('▶️ [useAudioPlayback] Attempting to play HTML5 audio');
        try {
          await audio.play();
          setIsPlaying(true);
          console.log('✅ [useAudioPlayback] HTML5 audio playing successfully');
        } catch (playError: any) {
          console.error('❌ [useAudioPlayback] Play failed:', playError.name, playError.message);
          
          if (playError.name === 'NotAllowedError' || playError.name === 'AbortError') {
            toast({
              title: "Audio Playback Blocked",
              description: "Click the play button again to start playback.",
              variant: "default",
            });
            setIsPlaying(false);
          } else {
            // Other errors: fall back to YouTube
            console.log('🔄 [useAudioPlayback] Falling back to YouTube due to play error');
            await fallbackToYouTube(track);
          }
        }
      } catch (error) {
        console.error('❌ [useAudioPlayback] HTML5 setup failed:', error);
        await fallbackToYouTube(track);
      }
    } else {
      // FALLBACK PATH: No preview URL, use YouTube
      console.log('🎬 [useAudioPlayback] No preview URL, using YouTube fallback');
      console.log('🎬 [useAudioPlayback] Setting playback mode to: youtube');
      await fallbackToYouTube(track);
    }

    async function fallbackToYouTube(track: Track) {
      setPlaybackMode('youtube');
      console.log('🔍 [useAudioPlayback] Searching YouTube for:', `${track.name} by ${track.artist}`);
      console.log('🔍 [useAudioPlayback] About to invoke youtube-search edge function');
      
      try {
        // Get the session ID from sessionStorage to verify it's available
        const currentSessionId = sessionStorage.getItem('session_id');
        console.log('📋 [useAudioPlayback] Current session ID:', currentSessionId);
        
        const { data: ytData, error: ytError } = await supabase.functions.invoke('youtube-search', {
          body: { 
            trackName: track.name, 
            artistName: track.artist 
          }
        });
        
        console.log('✅ [useAudioPlayback] youtube-search function completed');
        console.log('📊 [useAudioPlayback] Response data:', ytData);
        console.log('❌ [useAudioPlayback] Response error:', ytError);
        
        console.log('🎬 [useAudioPlayback] YouTube search response:', { 
          hasData: !!ytData, 
          videoId: ytData?.videoId,
          hasError: !!ytError,
          error: ytError 
        });
        
        if (ytError) {
          console.error('❌ [useAudioPlayback] YouTube search error:', ytError);
          
          // Check if it's a rate limit error
          if (ytError.message && ytError.message.includes('Too many requests')) {
            throw new Error('Too many requests. Please wait a moment and try again.');
          }
          
          throw ytError;
        }

        if (ytData?.videoId) {
          console.log('✅ [useAudioPlayback] Found YouTube video:', ytData.videoId);
          setCurrentTrack({ ...track, youtubeId: ytData.videoId });
          setIsPlaying(true);
          console.log('🎥 [useAudioPlayback] YouTubePlayer should now start playing');
        } else {
          console.error('❌ [useAudioPlayback] No videoId in response');
          throw new Error('No YouTube video found');
        }
      } catch (error: any) {
        console.error('❌ [useAudioPlayback] YouTube search failed:', error);
        console.error('❌ [useAudioPlayback] Error details:', {
          message: error?.message,
          status: error?.status,
          statusText: error?.statusText,
          context: error?.context,
          fullError: JSON.stringify(error, null, 2)
        });
        setIsPlaying(false);
        
        let errorMessage = "Could not load this track for playback";
        if (error?.message) {
          errorMessage += `: ${error.message}`;
        }
        
        toast({
          title: "Preview Unavailable",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  }, [toast, onTrackEnd]);

  const togglePlayPause = useCallback(() => {
    if (!currentTrack) return;

    if (playbackMode === 'html5' && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            toast({
              title: "Audio Playback Blocked",
              description: "Click play again to resume.",
            });
          }
        });
        setIsPlaying(true);
      }
    } else if (playbackMode === 'youtube') {
      // For YouTube, just toggle the state - YouTubePlayer will react
      console.log(isPlaying ? '⏸️ [useAudioPlayback] Pausing YouTube' : '▶️ [useAudioPlayback] Resuming YouTube');
      setIsPlaying(!isPlaying);
    }
  }, [currentTrack, isPlaying, playbackMode, toast]);

  const stopPlayback = useCallback(() => {
    console.log('⏹️ [useAudioPlayback] Stopping playback');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setCurrentTrack(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setProgress(0);
    setDuration(0);
    setPlaybackMode('html5');
  }, []);

  // This is called by YouTubePlayer when in youtube mode
  const handleProgress = useCallback((time: number, totalDuration: number) => {
    if (playbackMode === 'youtube') {
      setCurrentTime(time);
      setDuration(totalDuration || 30);
      const safeDuration = totalDuration || 30;
      setProgress(safeDuration > 0 ? (time / safeDuration) * 100 : 0);
    }
  }, [playbackMode]);

  return {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    progress,
    playbackMode,
    playTrack,
    togglePlayPause,
    stopPlayback,
    handleProgress,
  };
};
